import React from 'react';
import { Link } from 'react-router-dom';
import { Car, Search, Sparkles } from 'lucide-react';

interface SorteoAutoCardProps {
  habilitaSorteo: boolean;
  imagenPremioUrl?: string | null;
  tituloSorteo?: string | null;
  descripcionPremio?: string | null;
  idCongreso?: number;
}

export const SorteoAutoCard: React.FC<SorteoAutoCardProps> = ({
  habilitaSorteo,
  imagenPremioUrl,
  tituloSorteo,
  descripcionPremio,
  idCongreso
}) => {
  if (!habilitaSorteo) return null;

  return (
    <section className="py-20 bg-slate-50 relative border-t border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#001D36] via-brand-navy to-[#082B4D] border border-emerald-500/30 p-8 sm:p-12 shadow-2xl text-white">
          {/* Glows ambientales sutiles alejados del texto */}
          <div className="absolute top-0 right-0 -mt-12 -mr-12 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 -mb-12 -ml-12 w-64 h-64 bg-brand-cyan/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Texto y Bases */}
            <div className="lg:col-span-7 space-y-4 text-left">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 text-xs font-bold uppercase tracking-wider">
                <Car className="w-4 h-4 text-emerald-400" />
                <span>Beneficio Exclusivo Matriculados</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
                {tituloSorteo || 'Sorteo Oficial de Auto 0KM'}
              </h2>

              <p className="text-slate-200 text-sm sm:text-base leading-relaxed max-w-xl">
                {descripcionPremio || 'Durante las jornadas del Congreso en Mar del Plata se llevará a cabo el sorteo de un vehículo 0KM entre todos los colegiados matriculados habilitados y acreditados según la reglamentación del Colegio de Martilleros y Corredores Públicos de la Provincia de Buenos Aires.'}
              </p>

              {/* Más chances de ganar - Bloque orgánico y compacto */}
              <div className="flex items-start gap-2.5 p-3 rounded-xl bg-emerald-950/40 border border-emerald-500/25 max-w-xl text-slate-200">
                <Sparkles className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <p className="text-xs sm:text-sm leading-relaxed">
                  <strong className="text-emerald-300 font-bold">¡Multiplicá tus chances de ganar!</strong> Con tus aportes al día ya participás, y podés sumar hasta <span className="text-white font-semibold underline decoration-emerald-400/50">2 chances</span> anticipando el pago del ABAO del 1.º y 2.º semestre.
                </p>
              </div>

              {/* Botón Padrón Habilitado */}
              <div className="pt-2 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <Link
                  to={idCongreso ? `/evento/${idCongreso}/sorteo/padron` : '/sorteo/padron'}
                  className="inline-flex items-center gap-2.5 px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs sm:text-sm shadow-lg shadow-emerald-950/40 hover:shadow-emerald-500/25 transition-all duration-200 group"
                >
                  <Search className="w-4 h-4 text-emerald-200 group-hover:scale-110 transition-transform" />
                  <span>Consultar Padrón de Habilitados</span>
                </Link>
              </div>

              {/* Bases y Condiciones */}
              <div className="pt-3 border-t border-white/10 max-w-xl space-y-1.5 text-xs text-slate-300 leading-relaxed font-normal">
                <p>
                  Participan todos los Martilleros y Corredores Públicos matriculados de la Provincia de Buenos Aires que reúnen las siguientes condiciones generales de forma automática, sin necesidad de inscribirse ni tener que realizar ningún trámite.
                </p>
                <p>
                  <strong className="text-slate-100 font-semibold">Condiciones para participar:</strong> estar activo en la matrícula y tener una antigüedad de 1 año o más en el ejercicio profesional; estar al día con sus aportes previsionales y no tener convenios vigentes.
                </p>
              </div>
            </div>

            {/* Gráfica o Card del Premio */}
            <div className="lg:col-span-5 flex justify-center">
              {imagenPremioUrl ? (
                <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-slate-900/30 p-4 shadow-xl backdrop-blur-sm max-h-80 flex items-center justify-center">
                  <img
                    src={imagenPremioUrl}
                    alt="Auto 0KM Congreso"
                    className="w-full h-auto object-contain max-h-72 drop-shadow-2xl hover:scale-105 transition-transform duration-300"
                  />
                </div>
              ) : (
                <div className="w-full max-w-sm p-8 rounded-2xl bg-slate-900/50 border border-emerald-500/30 flex flex-col items-center text-center shadow-xl backdrop-blur-sm">
                  <div className="w-20 h-20 rounded-2xl bg-emerald-500/20 flex items-center justify-center text-emerald-400 mb-4 border border-emerald-500/40">
                    <Car className="w-10 h-10" />
                  </div>
                  <h4 className="font-heading font-extrabold text-xl text-white mb-1">
                    Auto 0KM 2026
                  </h4>
                  <p className="text-xs text-slate-300">
                    El gran premio de la edición oficial Mar del Plata.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
